import { track } from "@amplitude/analytics-react-native";
import { useApolloClient } from "@apollo/client";
import { ForwardedRef, forwardRef, useImperativeHandle, useRef } from "react";
import { amp } from "src/api/amplitude";
import parseFileToAsset from "src/api/asset-persist/helpers/parseFileToAsset.web";
import queueUpload from "src/api/asset-persist/queueUpload/queueUpload";
import ValidFileUploadTypes, {
	ValidFileUploadExtensions,
} from "src/constants/validFileUploadTypes";
import { useGatheringStore } from "src/contexts/GatheringContext";
import getFilteredUploadFileTypes from "src/helpers/fileHelpers/getFilteredUploadFileTypes";
import getRandomString from "src/helpers/getRandomString";
import toastUnsupportedFileTypes from "src/helpers/toastUnsupportedFileTypes";
import shouldBeThumbnail from "./helpers/shouldBeThumbnail";
import useUploadAgreement from "./hooks/useUploadAgreement";
import { UploadPhotosPromptHandler, UploadPhotosPromptProps } from "./types";

const validFileUploadAccept = Array.from(ValidFileUploadTypes)
	.concat(Array.from(ValidFileUploadExtensions).map((ext) => `.${ext}`))
	.join(",");

const UploadPhotosPrompt = (
	props: UploadPhotosPromptProps,
	ref: ForwardedRef<UploadPhotosPromptHandler>,
) => {
	const fileInputRef = useRef<HTMLInputElement>(null);
	const gatheringId = useGatheringStore((state) => state.gatheringId);
	const ownerId = useGatheringStore((state) => state.ownerId);
	const numPhotos = useGatheringStore((state) => state.numPhotos);
	const apolloClient = useApolloClient();

	useImperativeHandle(ref, () => ({
		promptUploadPhotos: async () => {
			fileInputRef.current?.click();
		},
	}));
	const { checkUploadAgreement } = useUploadAgreement();

	return (
		<input
			ref={fileInputRef}
			type="file"
			accept={validFileUploadAccept}
			multiple
			style={{
				display: "none",
			}}
			onChange={async (e) => {
				const { validFiles, numUnsupportedFiles, unsupportedFileTypes } =
					getFilteredUploadFileTypes(e.target.files);
				if (numUnsupportedFiles > 0) {
					toastUnsupportedFileTypes({
						numUnsupportedFiles,
						unsupportedFileTypes,
					});
				}
				if (validFiles.length === 0) return;

				const { shouldContinue, onUpload } = await checkUploadAgreement();
				if (!shouldContinue) return;

				track(amp.photo.uploadStart, {
					method: "button",
					dataLength: validFiles.length,
				});

				const selectionId = getRandomString();
				await queueUpload(
					validFiles.map((file, i) => ({
						...parseFileToAsset(file, {
							localSelectionId: selectionId,
							localSelectionIndex: i,
						}),
						localSelectionId: selectionId,
						localSelectionIndex: i,
						gatheringId,
						isThumbnail: shouldBeThumbnail({
							numPhotos,
							i,
							ownerId,
							gatheringId,
						}),
					})),
					{
						apolloClient,
					},
				);
				onUpload?.();
			}}
		/>
	);
};

export default forwardRef(UploadPhotosPrompt);
